import { useEffect, useState } from "react";
import { useAdsWorkspaceStore, type SubAgentId } from "@/features/ads";
import { getAccessToken, resolveApiUrl } from "@/lib/jwt-auth-client";
import { getCompanionSubAgent, SESSION_CAPABLE_SUB_AGENT_IDS } from "../../lib/chatAgentRegistry";

export interface AgentSessionsSummary {
  count: number;
  lastUsedAt: string | null;
}

const SUMMARY_AGENT_IDS: SubAgentId[] = SESSION_CAPABLE_SUB_AGENT_IDS;

type SummaryMap = Record<SubAgentId, AgentSessionsSummary>;

const EMPTY: SummaryMap = Object.fromEntries(
  SUMMARY_AGENT_IDS.map((id) => [id, { count: 0, lastUsedAt: null }]),
) as SummaryMap;

async function fetchSummary(
  agentId: SubAgentId,
  workspaceId: string | null,
): Promise<AgentSessionsSummary> {
  // No workspace = no scope to summarise against — skip the request.
  if (!workspaceId) return { count: 0, lastUsedAt: null };
  try {
    const token = await getAccessToken();
    const meta = getCompanionSubAgent(agentId);
    const runtimeTarget = meta.runtimeTarget ?? "anthropic_sdk";
    const backendAgentName =
      runtimeTarget === "unified" ? meta.agentName ?? meta.backendAgentId : meta.backendAgentId;
    const res =
      runtimeTarget === "unified"
        ? await fetch(
            resolveApiUrl(
              `/core/agent/workspace/${encodeURIComponent(
                workspaceId,
              )}/sessions?${new URLSearchParams({
                agentName: backendAgentName,
                limit: "50",
              }).toString()}`,
            ),
            { headers: token ? { Authorization: `Bearer ${token}` } : {} },
          )
        : await fetch(
            resolveApiUrl(
              `/core/agent/managed-chat/sessions?${new URLSearchParams({
                agentId: backendAgentName,
                workspaceId,
              }).toString()}`,
            ),
            { headers: token ? { Authorization: `Bearer ${token}` } : {} },
          );
    if (!res.ok) return { count: 0, lastUsedAt: null };
    const data = await res.json();
    const sessions: Array<{ createdAt?: unknown }> = Array.isArray(data?.sessions) ? data.sessions : [];
    let lastUsedAt: string | null = null;
    for (const s of sessions) {
      const ts = typeof s?.createdAt === "string" ? s.createdAt : null;
      if (!ts) continue;
      if (!lastUsedAt || ts > lastUsedAt) lastUsedAt = ts;
    }
    return { count: sessions.length, lastUsedAt };
  } catch {
    return { count: 0, lastUsedAt: null };
  }
}

/**
 * Fetches session count + latest-session timestamp for the managed sub-agents
 * (Banan, Vid, Scout, GAds, Mads — Meta Ads, Vidzz — Higgsfield), scoped to
 * the active workspace. Refetches when any agent's live sessionId changes
 * (so a freshly created session shows up without a reload) or when the
 * workspace scope switches.
 */
export function useAgentSessionsSummary(): SummaryMap {
  const bananSessionId = useAdsWorkspaceStore((s) => s.subAgentChats.banan.sessionId);
  const vidSessionId = useAdsWorkspaceStore((s) => s.subAgentChats.vid.sessionId);
  const scoutSessionId = useAdsWorkspaceStore((s) => s.subAgentChats.scout.sessionId);
  const gadsSessionId = useAdsWorkspaceStore((s) => s.subAgentChats.gads.sessionId);
  const metaAdsSessionId = useAdsWorkspaceStore((s) => s.subAgentChats.meta_ads.sessionId);
  const higgsfieldSessionId = useAdsWorkspaceStore((s) => s.subAgentChats.higgsfield.sessionId);
  const higgsfieldCliSessionId = useAdsWorkspaceStore(
    (s) => s.subAgentChats.higgsfield_cli.sessionId,
  );
  const agentVSessionId = useAdsWorkspaceStore((s) => s.subAgentChats.agent_v.sessionId);
  const moodboardSessionId = useAdsWorkspaceStore((s) => s.subAgentChats.moodboard.sessionId);
  const scopeWorkspaceId = useAdsWorkspaceStore((s) => s.scopeWorkspaceId);
  const revealSubAgent = useAdsWorkspaceStore((s) => s.revealSubAgent);
  const [summary, setSummary] = useState<SummaryMap>(EMPTY);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        SUMMARY_AGENT_IDS.map((id) => fetchSummary(id, scopeWorkspaceId)),
      );
      if (cancelled) return;
      const next = SUMMARY_AGENT_IDS.reduce<SummaryMap>((acc, id, i) => {
        acc[id] = results[i];
        return acc;
      }, { ...EMPTY });
      setSummary(next);
      // Backfill the chat list from the durable backend session list: any
      // sub-agent triggered in this workspace gets re-revealed on load, so the
      // row survives page refresh / new tab / new browser. Scoped per workspace
      // because the fetch itself is scoped by workspaceId.
      for (const id of SUMMARY_AGENT_IDS) {
        if (next[id].count > 0) revealSubAgent(id);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    bananSessionId,
    vidSessionId,
    scoutSessionId,
    gadsSessionId,
    metaAdsSessionId,
    higgsfieldSessionId,
    higgsfieldCliSessionId,
    agentVSessionId,
    moodboardSessionId,
    scopeWorkspaceId,
    revealSubAgent,
  ]);

  return summary;
}
